import React, {createContext} from 'react';
import { gql } from 'apollo-boost';
import { useQuery } from "@apollo/react-hooks";
import {useCurrentUser} from './CurrentUserProvider';

/** @import {QueryResult} from '@apollo/react-common' */
/** @import { CustomerAndAccounts, CustomerAndAccountsResponse, CustomerDataContextType, CustomerDataProviderComponent, NewAccount, NewAccountResponse } from "./CustomerDataProviderTypes" */

export const customerDataContext = createContext(
  /** @type {CustomerDataContextType | null} */ (null)
);

export const GET_CUSTOMER_AND_ACCOUNTS = gql`
    query GetCustomerAndAccounts($id : ID!){
        customer(id:$id) {
            accounts {
                accountNumber
                balance
                availableBalance
                interestAccrued
                closureFee
                id
                linkedAccountNumber
                linkedAccountSortCode
                interestPaid
                product {
                    interestRate
                    name
                    maxWithdrawalLimit
                    lowerInterestRateAmount
                    stepUpAmount
                    productType
                    productTerm {
                      value
                      unit
                    }
                    code
                }
                sortCode
                accountStatus
                fundingEndDate
                fundingWindowStartDate
                coolingOffEndDate
                transactions {
                  reference
                  transactionId,
                  amount
                  updatedBalance
                  bookingDate,
                  counterpartAccountNumber,
                  counterpartSortCode,
                  action,
                  interestPeriodFrom,
                  interestPeriodTo
                }
                pendingTransactions {
                  pendingTransactionId
                  counterpartAccountNumber
                  counterpartSortCode
                  counterpartAccountType
                  toLinkedAccount
                  amount
                  withdrawalDate
                  state
                  closeAccount
                  allowClosure
                }
                transferAccounts {
                  id
                  accountNumber
				  sortCode
                  balance
                  product {
                    productType
                    interestRate
                    name
                    maxWithdrawalLimit
                    lowerInterestRateAmount
                    stepUpAmount
                    interestRateTiers {
                      minBalance
                      maxBalance
                      rate
                    }
                  }
                }
                maturity {
                  maturityInstructionWindowStatus
                  maturityInstruction
                  maturityWindowCloseDate
                  maturityWindowCloseTime
                  maturityDate
                  destinationAccountId
                  calculatedBalanceAtMaturity
                  calculatedTotalInterest
                  rolloverAccountProductId
                  remainingBalanceWithdrawalAmount
                  partialRolloverAmount
                  availableRolloverRates {
                    productId
                    rate
                  }
                }
                notice {
                	noticePeriod
                    noticePeriodUnit
                }
                transferIn {
                    status
                    lastUpdatedAt
                }
                transferOut {
                    status
                    lastUpdatedAt
                }
            }
            firstName
            id
            lastName
            middleName
            email
            mobileNumber
            nationalInsuranceNumber
            applicationStatus
            address {
              flatNumber
              buildingNumber
              buildingName,
              streetLine1
              streetLine2
              town
              country
              postCode
            }
            smsCommunication
            emailCommunication
            onlineBankingCommunication
            newAccounts {
              productId
              qualify
              productName
              productRate
              productAerRate
              productCode
              productData
              productType           
            }
            products {
              id
              name
              issueNumber
              rate
              aerRate
              code
              type
              rateChanges {
                rate
                aerRate
                issue
                scheduledFor
                reason
                rateNotificationAt
              }
              rateChangeNotification
              summaryBoxFileName
              productTermsFileName
              productTerm {
                value
                unit
              }
            }
            isaAllowance {
              taxYear
              totalAllowance
              usedAllowance
              remainingAllowance
              holdAllowance
            }
        }
    }  `;

/**
 * 
 * @param {NewAccountResponse} acc
 * @returns {NewAccount}
 */
const mapNewAccount = (acc) => {
  const { productData, ...otherProps } = acc;
  /** @type {NewAccount} */
  const mappedAccount = {
    ...otherProps,
    productData: productData ? productData.reduce((acc, item) => {
      acc[item.Key] = item.Value;
      return acc;
    }, /** @type {Record<string, unknown>} */ ({})) : {},
  };

  return mappedAccount;

  /* OLD LOGIC - LEFT FOR REFERENCE

  if (acc.productData && acc.productData.length > 0) {
    let newData = {};
    for (let i = 0; i < acc.productData.length; i++) {
      if (acc.productData[i].Key) {
        newData[acc.productData[i].Key] = acc.productData[i].Value;
      }
    }
    acc.productData = newData;
  }
  return acc;
  
  */
}

/**
 * HACK: because graphQL doesnt support generic types
 * @param {CustomerAndAccountsResponse} data 
 * @returns {CustomerAndAccounts}
 */
const map = (data) => {
  const { newAccounts, ...otherCustomerProps } = data.customer;
  const mappedNewAccounts = newAccounts ? newAccounts.map(mapNewAccount) : [];

  /** @type {CustomerAndAccounts} */
  const mappedData = {
    customer: {
      ...otherCustomerProps,
      newAccounts: mappedNewAccounts
    }
  }

  return mappedData;

  /* OLD LOGIC - LEFT FOR REFERENCE

  if (!data) return data;
  if (!data.customer.newAccounts) return data;
  for (var i = 0; i < data.customer.newAccounts.length; i++) {
    data.customer.newAccounts[i] = mapNewAccount(data.customer.newAccounts[i]);
  }
  return data;
  
  */
}

/** @type {CustomerDataProviderComponent} */
const CustomerDataContext = props => {
  const currentUser = useCurrentUser();

	/** @type {QueryResult<CustomerAndAccountsResponse>} */
  const { loading, error, data } = useQuery(GET_CUSTOMER_AND_ACCOUNTS, {
    variables: { id: currentUser ? currentUser.customerId : '' },
    skip: !currentUser || !currentUser.customerId
  });

  if (error) {
    console.error('Problem loading customer and accounts', error);
  }

  return (
      <customerDataContext.Provider value={{
        data: data ? map(data) : null,
        loading: loading
      }}>
        {props.children}
      </customerDataContext.Provider>
  )
};

export default CustomerDataContext;
